<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-0 sm:p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <router-link to="/survey-and-feedback-center"
                        class="hover:text-blue-700 transition font-medium">Survey & Feedback Center</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Smart Feedback Dashboard</span>
                </nav>

                <!-- Header -->
                <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6">
                    <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900 mb-2">Smart Feedback Analytics</h1>
                            <p class="text-gray-600">Monitor stakeholder sentiment, engagement, and detailed responses
                                in one place.</p>
                        </div>
                        <div class="flex items-center gap-3">
                            <div
                                class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">
                                <font-awesome-icon icon="chart-line" class="mr-2"></font-awesome-icon>
                                Live Dashboard
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 mb-8">
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Date From</label>
                            <date-picker v-model:value="filters.dateFrom" format="YYYY-MM-DD" type="date"
                                value-type="format" placeholder="Select start date" @change="handleFilterChange"
                                class="w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Date To</label>
                            <date-picker v-model:value="filters.dateTo" format="YYYY-MM-DD" type="date"
                                value-type="format" placeholder="Select end date" @change="handleFilterChange"
                                class="w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Stakeholder Type</label>
                            <select v-model="filters.stakeholderType" @change="handleFilterChange"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                                <option value="">All Roles</option>
                                <option v-for="role in roleOptions" :key="role.value" :value="role.value">
                                    {{ role.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Trend Days</label>
                            <select v-model="filters.trendDays" @change="handleFilterChange"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                                <option :value="7">Last 7 days</option>
                                <option :value="30">Last 30 days</option>
                                <option :value="90">Last 90 days</option>
                                <option :value="180">Last 180 days</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Overall Statistics Cards -->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                    <div v-for="card in overallStatCards" :key="card.key"
                        class="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg px-6 py-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <p class="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                                    {{ card.label }}</p>
                                <div v-if="isLoading" class="mt-3 h-8 w-24 rounded bg-gray-200 animate-pulse"></div>
                                <p v-else class="mt-3 text-3xl font-bold text-gray-900">{{ card.value }}</p>
                                <p v-if="card.subtitle" class="mt-2 text-xs text-gray-500">{{ card.subtitle }}</p>
                            </div>
                            <div
                                :class="`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.accent} text-white flex items-center justify-center shadow-inner`">
                                <font-awesome-icon :icon="card.icon" class="text-lg"></font-awesome-icon>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Star Rating Distribution (like Google reviews) -->
                <div class="bg-white rounded-2xl border shadow-lg px-6 sm:px-8 py-5 mb-8">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <div>
                            <h2 class="text-lg sm:text-xl font-bold text-gray-900">Rating Breakdown</h2>
                            <p class="text-sm text-gray-500">
                                Distribution of quick rating responses across 1–5 stars.
                            </p>
                        </div>
                        <div class="text-sm text-gray-500">
                            <span>Total ratings: </span>
                            <span class="font-semibold text-gray-900">
                                {{ formatNumber(overallStats.total_quick_ratings) }}
                            </span>
                        </div>
                    </div>
                    <div v-if="overallStats.total_quick_ratings === 0" class="py-4 text-sm text-gray-500">
                        No rating data available yet.
                    </div>
                    <div v-else class="space-y-2">
                        <div v-for="row in ratingBreakdown" :key="row.stars"
                            class="flex items-center gap-3 text-sm text-gray-700">
                            <div class="w-12 flex items-center gap-1">
                                <span class="font-medium">{{ row.stars }}</span>
                                <font-awesome-icon icon="star" class="text-amber-400 text-xs" />
                            </div>
                            <div class="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                                <div class="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-400"
                                    :style="{ width: row.barWidth }"></div>
                            </div>
                            <div class="w-24 text-right tabular-nums">
                                <span class="font-semibold text-gray-900">{{ row.count }}</span>
                                <span class="text-xs text-gray-500 ml-1">({{ row.percentage }}%)</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Detailed Questions Rating Breakdown -->
                <div class="bg-white rounded-2xl border shadow-lg px-6 sm:px-8 py-5 mb-8">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <div>
                            <h2 class="text-lg sm:text-xl font-bold text-gray-900">Detailed Question Ratings</h2>
                            <p class="text-sm text-gray-500">
                                Star distribution for each detailed feedback question.
                            </p>
                        </div>
                        <div class="text-sm text-gray-500" v-if="detailedQuestionBreakdown.length">
                            <span>Questions: </span>
                            <span class="font-semibold text-gray-900">
                                {{ detailedQuestionBreakdown.length }}
                            </span>
                        </div>
                    </div>

                    <div v-if="!detailedQuestionBreakdown.length" class="py-4 text-sm text-gray-500">
                        No detailed rating data available yet.
                    </div>

                    <div v-else class="space-y-3">
                        <div v-for="item in detailedQuestionBreakdown" :key="item.id"
                            class="rounded-2xl border border-gray-100 bg-slate-50/60 overflow-hidden">
                            <!-- Accordion header -->
                            <button type="button"
                                class="w-full px-4 py-3 sm:px-5 sm:py-3 flex items-center gap-3 sm:gap-4 text-left hover:bg-slate-100/70 transition-colors"
                                @click="toggleDetailedQuestion(item.id)">
                                <div class="flex-1">
                                    <p class="text-sm font-semibold text-gray-900 line-clamp-2">
                                        {{ item.question }}
                                    </p>
                                </div>
                                <div class="flex items-center gap-4 text-xs sm:text-sm text-gray-600">
                                    <div class="flex items-center gap-1">
                                        <span class="uppercase tracking-wide text-gray-500">Avg</span>
                                        <span class="font-semibold text-gray-900">{{ item.average }}</span>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <span class="uppercase tracking-wide text-gray-500">Total</span>
                                        <span class="font-semibold text-gray-900">{{ item.total }}</span>
                                    </div>
                                    <div
                                        class="ml-1 w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500">
                                        <font-awesome-icon
                                            icon="chevron-down"
                                            class="text-xs"
                                            :style="{
                                                transform: isDetailedQuestionExpanded(item.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 200ms ease'
                                            }"
                                        />
                                    </div>
                                </div>
                            </button>
                            <!-- Accordion body -->
                            <transition name="accordion">
                                <div v-if="isDetailedQuestionExpanded(item.id)"
                                    class="px-4 py-3 sm:px-5 sm:py-4 border-t border-gray-100 bg-slate-50/80 accordion-inner">
                                    <div class="space-y-1.5">
                                        <div v-for="row in item.rows" :key="row.stars"
                                            class="flex items-center gap-3 text-xs sm:text-sm text-gray-700">
                                            <div class="w-10 flex items-center gap-1">
                                                <span class="font-medium">{{ row.stars }}</span>
                                                <font-awesome-icon icon="star"
                                                    class="text-amber-400 text-[10px] sm:text-xs" />
                                            </div>
                                            <div class="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                                                <div
                                                    class="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-400"
                                                    :style="{ width: row.barWidth }"></div>
                                            </div>
                                            <div class="w-20 text-right tabular-nums">
                                                <span class="font-semibold text-gray-900">{{ row.count }}</span>
                                                <span class="text-[10px] sm:text-xs text-gray-500 ml-1">({{ row.percentage
                                                    }}%)</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>

                <!-- Charts Grid -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <!-- Rating Trends -->
                    <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
                        <h1 class="text-lg font-bold mb-4">Rating Trends</h1>
                        <div v-if="isLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
                        <div v-else-if="!hasTrendData"
                            class="ht-350 flex flex-col items-center justify-center text-gray-500">
                            <font-awesome-icon icon="chart-line" class="text-6xl mb-4 text-gray-300" />
                            <p class="text-lg font-medium">No data available</p>
                            <p class="text-sm mt-2">There are no rating trends to display</p>
                        </div>
                        <apexchart v-else type="line" height="350" :options="trendChartOptions" :series="trendSeries">
                        </apexchart>
                    </div>

                    <!-- Sentiment Distribution -->
                    <div class="bg-white rounded-2xl border shadow-lg px-8 py-6">
                        <h1 class="text-lg font-bold mb-4">Sentiment Distribution</h1>
                        <div v-if="isLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
                        <div v-else-if="!hasSentimentData"
                            class="ht-350 flex flex-col items-center justify-center text-gray-500">
                            <font-awesome-icon icon="chart-pie" class="text-6xl mb-4 text-gray-300" />
                            <p class="text-lg font-medium">No data available</p>
                            <p class="text-sm mt-2">There is no sentiment data to display</p>
                        </div>
                        <apexchart v-else type="pie" height="350" :options="sentimentChartOptions"
                            :series="sentimentSeries">
                        </apexchart>
                    </div>
                </div>

                <!-- Stakeholder Statistics -->
                <div class="bg-white rounded-2xl border shadow-lg px-8 py-6 mb-8">
                    <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900">Statistics by Stakeholder Type</h1>
                            <p class="text-sm text-gray-500 mt-1">Track engagement, satisfaction, and response behaviour
                                for each role.</p>
                        </div>
                        <div
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">
                            <font-awesome-icon icon="users"></font-awesome-icon>
                            <span>{{ stakeholderCards.length }} Roles</span>
                        </div>
                    </div>
                    <div v-if="stakeholderCards.length === 0" class="text-gray-500 text-center py-6">
                        No role statistics available.
                    </div>
                    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        <div v-for="card in stakeholderCards" :key="card.key"
                            class="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-xl transition-all duration-200 p-5">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-400">Role</p>
                                    <h3 class="text-xl font-semibold text-gray-900">{{ card.title }}</h3>
                                </div>
                                <!-- <div
                                    :class="`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.gradient} text-white flex items-center justify-center shadow-inner`">
                                    <font-awesome-icon :icon="card.icon" class="text-lg"></font-awesome-icon>
                                </div> -->
                            </div>
                            <div class="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div class="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
                                    <p class="text-xs text-gray-500 uppercase tracking-wide">Sessions</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ card.totalSessions }}</p>
                                </div>
                                <div class="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
                                    <p class="text-xs text-gray-500 uppercase tracking-wide">Ratings</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ card.totalRatings }}</p>
                                </div>
                                <div class="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
                                    <p class="text-xs text-gray-500 uppercase tracking-wide">Avg Rating</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ card.averageRating }}</p>
                                </div>
                                <div class="rounded-xl bg-slate-50/80 p-3 border border-slate-100">
                                    <p class="text-xs text-gray-500 uppercase tracking-wide">Engagement</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ card.responseRate }}%</p>
                                </div>
                            </div>
                            <div class="mt-5">
                                <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
                                    <span>Response rate</span>
                                    <span class="font-semibold text-gray-900">{{ card.responseRate }}%</span>
                                </div>
                                <div class="h-2 rounded-full bg-gray-100 overflow-hidden">
                                    <div class="h-full rounded-full bg-gradient-to-r from-primary to-blue-500"
                                        :style="{ width: card.responseRateWidth }"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Stakeholder Comparison Chart -->
                <div class="bg-white rounded-2xl border shadow-lg px-8 py-6 mb-8">
                    <h1 class="text-lg font-bold mb-4">Average Rating by Stakeholder</h1>
                    <div v-if="isLoading" class="ht-350 bg-gray-200 animate-pulse"></div>
                    <div v-else-if="!hasStakeholderChartData"
                        class="ht-350 flex flex-col items-center justify-center text-gray-500">
                        <font-awesome-icon icon="chart-bar" class="text-6xl mb-4 text-gray-300" />
                        <p class="text-lg font-medium">No data available</p>
                        <p class="text-sm mt-2">There is no stakeholder rating data to display</p>
                    </div>
                    <apexchart v-else type="bar" height="350" :options="stakeholderChartOptions"
                        :series="stakeholderSeries">
                    </apexchart>
                </div>

                <!-- Submitters List -->
                <div class="bg-white rounded-2xl border shadow-lg px-6 sm:px-8 py-6 space-y-4">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 class="text-2xl font-bold text-gray-900">Feedback Submitters</h2>
                        </div>
                        <div class="flex flex-col sm:items-end text-sm text-gray-500">
                            <span>Total users: <span class="font-semibold text-gray-900">{{ submitterPagination.total
                                    }}</span></span>
                        </div>
                    </div>
                    <div v-if="submittersLoading" class="py-12 flex justify-center">
                        <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
                    </div>
                    <div v-else-if="submitters.length === 0"
                        class="py-12 flex flex-col items-center justify-center text-gray-500 border border-dashed border-gray-200 rounded-2xl bg-gray-50">
                        <font-awesome-icon icon="users" class="text-6xl mb-4 text-gray-300" />
                        <p class="text-lg font-semibold text-gray-700">No submitters found</p>
                        <p class="text-sm mt-2">No feedback submitters match the current filters.</p>
                    </div>
                    <div v-else class="overflow-x-auto rounded-2xl border border-gray-100">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <th class="px-4 py-3">User</th>
                                    <th class="px-4 py-3">Role</th>
                                    <th class="px-4 py-3">Entity</th>
                                    <th class="px-4 py-3 text-center">Total</th>
                                    <th class="px-4 py-3 text-center">Initiated</th>
                                    <th class="px-4 py-3 text-center">Dismissed</th>
                                    <th class="px-4 py-3 text-center">Submitted</th>
                                    <th class="px-4 py-3 text-center">Detailed</th>
                                    <th class="px-4 py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 text-sm bg-white">
                                <tr v-for="submitter in submitters"
                                    :key="submitter.user ? submitter.user.id : submitter.user_id"
                                    class="border-b border-dashed border-gray-100 hover:bg-gray-50 transition-colors">
                                    <td class="px-4 py-3">
                                        <div>
                                            <p class="font-semibold text-gray-900">
                                                {{ submitter.user && submitter.user.name ? submitter.user.name :
                                                    'Unknown User'
                                                }}
                                            </p>
                                            <p class="text-xs text-gray-500">
                                                {{ submitter.user && submitter.user.email ? submitter.user.email : 'N/A'
                                                }}
                                            </p>
                                        </div>
                                    </td>
                                    <td class="px-4 py-3 text-sm">
                                        <span v-if="getSubmitterRoleLabel(submitter)"
                                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                                            {{ getSubmitterRoleLabel(submitter) }}
                                        </span>
                                        <span v-else class="text-gray-400">N/A</span>
                                    </td>
                                    <td class="px-4 py-3 text-sm text-gray-700">
                                        {{ submitter.user && submitter.user.entity ? submitter.user.entity : 'N/A' }}
                                    </td>
                                    <td class="px-4 py-3 text-center font-semibold text-gray-900">
                                        {{ submitter.total_feedback || 0 }}
                                    </td>
                                    <td class="px-4 py-3 text-center text-gray-700">
                                        {{ submitter.initiated_count || 0 }}
                                    </td>
                                    <td class="px-4 py-3 text-center text-gray-700">
                                        {{ submitter.dismissed_count || 0 }}
                                    </td>
                                    <td class="px-4 py-3 text-center text-gray-700">
                                        {{ submitter.submitted_count || 0 }}
                                    </td>
                                    <td class="px-4 py-3 text-center text-gray-700">
                                        {{ submitter.detailed_submitted_count || 0 }}
                                    </td>
                                    <td class="px-4 py-3 text-center">
                                        <div @click="openSubmitterDetails(submitter)" role="button" tabindex="0"
                                            @keydown.enter.prevent="openSubmitterDetails(submitter)"
                                            @keydown.space.prevent="openSubmitterDetails(submitter)"
                                            class="bg-gray-100 hover:bg-blue-50 w-10 h-10 flex justify-center items-center rounded-lg group cursor-pointer mx-auto shadow-sm transition"
                                            title="View details">
                                            <icon-eye
                                                class="w-4 h-4 text-primary transform group-hover:text-blue-700 group-hover:scale-110 transition">
                                            </icon-eye>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-500 mt-4"
                        v-if="submitterPagination.total > 0">
                        <pagination v-if="submitterPagination.lastPage > 1" class="w-full"
                            :paginatorInfo="submitterPagination" :setPage="setSubmitterPage"></pagination>
                    </div>
                </div>

                <!-- Detail Modal -->
                <base-full-modal v-if="showDetailModal" :title="detailModalTitle" @close="closeDetailModal">
                    <template #default>
                        <div class="space-y-5 text-left p-8">
                            <div
                                class="rounded-2xl border border-gray-100 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm">
                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <p class="text-xs uppercase tracking-widest text-primary font-semibold">
                                            Submitter
                                            Details</p>
                                        <h3 class="text-2xl font-bold text-gray-900">
                                            {{
                                                selectedSubmitter && selectedSubmitter.user ? selectedSubmitter.user.name :
                                                    'Userx'
                                            }}
                                        </h3>
                                        <div v-if="selectedSubmitter && selectedSubmitter.user"
                                            class="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                            <span>{{ selectedSubmitter.user.email }}</span>
                                            <span v-if="selectedSubmitter.user.entity"
                                                class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                                {{ selectedSubmitter.user.entity }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="flex flex-wrap gap-2">
                                        <span v-if="getSubmitterRoleLabel(selectedSubmitter)"
                                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                                            {{ getSubmitterRoleLabel(selectedSubmitter) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm text-gray-600">
                                    <div class="rounded-xl border border-dashed border-gray-200 bg-white/80 p-3">
                                        <p class="text-xs uppercase tracking-widest text-gray-400">Total</p>
                                        <p class="text-lg font-semibold text-gray-900">{{
                                            selectedSubmitter.total_feedback || 0
                                            }}</p>
                                    </div>
                                    <div class="rounded-xl border border-dashed border-gray-200 bg-white/80 p-3">
                                        <p class="text-xs uppercase tracking-widest text-gray-400">Initiated</p>
                                        <p class="text-lg font-semibold text-amber-600">{{
                                            selectedSubmitter.initiated_count ||
                                            0 }}</p>
                                    </div>
                                    <div class="rounded-xl border border-dashed border-gray-200 bg-white/80 p-3">
                                        <p class="text-xs uppercase tracking-widest text-gray-400">Submitted</p>
                                        <p class="text-lg font-semibold text-emerald-600">{{
                                            selectedSubmitter.submitted_count
                                            || 0 }}</p>
                                    </div>
                                    <div class="rounded-xl border border-dashed border-gray-200 bg-white/80 p-3">
                                        <p class="text-xs uppercase tracking-widest text-gray-400">Detailed</p>
                                        <p class="text-lg font-semibold text-blue-600">{{
                                            selectedSubmitter.detailed_submitted_count || 0 }}</p>
                                    </div>
                                </div>
                            </div>

                            <div v-if="detailLoading" class="py-12 flex justify-center">
                                <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
                            </div>
                            <div v-else-if="detailSessions.length === 0"
                                class="py-12 flex flex-col items-center justify-center text-gray-500">
                                <font-awesome-icon icon="inbox" class="text-6xl mb-4 text-gray-300" />
                                <p class="text-lg font-medium">No feedback entries</p>
                                <p class="text-sm mt-2">This user has no feedback entries matching the current filters.
                                </p>
                            </div>
                            <div v-else class="space-y-4">
                                <div v-for="session in detailSessions" :key="session.id"
                                    class="border border-gray-100 rounded-2xl p-4 shadow-sm bg-white">
                                    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                        <div>
                                            <p class="text-sm text-gray-500">Date</p>
                                            <p class="text-base font-semibold text-gray-900">{{
                                                formatDateTime(session.created_at) }}</p>
                                        </div>
                                        <div class="mt-3 sm:mt-0 flex flex-wrap gap-4 text-sm text-gray-700">
                                            <span>Status: <strong class="text-gray-900">{{ formatStatus(session.status)
                                                    }}</strong></span>
                                            <span>Quick Rating: <strong class="text-gray-900">{{
                                                formatRating(session.quick_rating) }}</strong></span>
                                            <span>Sentiment: <strong class="text-gray-900">{{ session.sentiment_label ||
                                                'N/A'
                                                    }}</strong></span>
                                        </div>
                                    </div>
                                    <div class="mt-3 text-sm text-gray-700" v-if="session.comment">
                                        <p class="font-semibold text-gray-800 mb-1">Comment</p>
                                        <p class="bg-gray-50 border border-gray-200 rounded-lg p-3">{{ session.comment
                                            }}</p>
                                    </div>
                                    <div class="mt-4" v-if="session.responses && session.responses.length">
                                        <p class="text-sm font-semibold text-gray-800 mb-2">Detailed Ratings</p>
                                        <div class="space-y-2">
                                            <div v-for="response in session.responses" :key="response.question_id"
                                                class="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                                <span class="font-medium">{{ response.question &&
                                                    response.question.question ?
                                                    response.question.question : `Question ${response.question_id}`
                                                    }}</span>
                                                <span class="font-semibold text-gray-900">{{
                                                    formatRating(response.rating)
                                                    }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <pagination v-if="detailPagination.lastPage > 1" class="pt-4"
                                    :paginatorInfo="detailPagination" :setPage="setDetailPage"></pagination>
                            </div>
                        </div>
                    </template>
                </base-full-modal>
            </div>
        </div>
    </div>
</template>

<script>
import { gql } from 'graphql-tag';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import Pagination from '../../components/shared/pagination.vue';
import IconEye from '../../components/icons/IconEye.vue';

const GET_DASHBOARD_STATS = gql`
    query GetFeedbackDashboardStats(
        $date_from: DateTime
        $date_to: DateTime
        $stakeholder_type: String
        $trend_days: Int
    ) {
        getFeedbackDashboardStats(
            date_from: $date_from
            date_to: $date_to
            stakeholder_type: $stakeholder_type
            trend_days: $trend_days
        ) {
            error
            error_code
            message
            data {
                overall {
                    total_sessions
                    total_quick_ratings
                    total_detailed_submissions
                    average_quick_rating
                    average_detailed_rating
                    response_rate
                    initiated_sessions
                    dismissed_sessions
                    rating_1_count
                    rating_2_count
                    rating_3_count
                    rating_4_count
                    rating_5_count
                }
                detailed_questions {
                    question_id
                    question
                    rating_1_count
                    rating_2_count
                    rating_3_count
                    rating_4_count
                    rating_5_count
                    total_ratings
                    average_rating
                }
                by_stakeholder {
                    role_name
                    role_display_name
                        total_sessions
                        total_ratings
                        average_rating
                        response_rate
                }
                sentiment {
                    positive
                    negative
                    neutral
                }
                trends {
                    date
                    average_rating
                    response_count
                }
            }
        }
    }
`;

const GET_FEEDBACK_SUBMITTERS = gql`
    query GetFeedbackSubmitters(
        $date_from: DateTime
        $date_to: DateTime
        $stakeholder_type: String
        $first: Int!
        $page: Int!
    ) {
        smartFeedbackSubmitters(
            date_from: $date_from
            date_to: $date_to
            stakeholder_type: $stakeholder_type
            first: $first
            page: $page
        ) {
            error
            error_code
            message
            data {
                user {
                    id
                    name
                    email
                    entity
                }
                total_feedback
                initiated_count
                dismissed_count
                submitted_count
                detailed_submitted_count
                role_name
                role_display_name
            }
            paginatorInfo {
                total
                count
                perPage
                currentPage
                lastPage
                firstItem
                lastItem
                hasMorePages
            }
        }
    }
`;

const GET_FEEDBACK_USER_SESSIONS = gql`
    query GetFeedbackUserSessions(
        $user_id: ID!
        $date_from: DateTime
        $date_to: DateTime
        $stakeholder_type: String
        $first: Int!
        $page: Int!
    ) {
        smartFeedbackUserSessions(
            user_id: $user_id
            date_from: $date_from
            date_to: $date_to
            stakeholder_type: $stakeholder_type
            first: $first
            page: $page
        ) {
            error
            error_code
            message
            data {
                id
                stakeholder_type
                quick_rating
                comment
                sentiment_label
                sentiment_score
                status
                created_at
                responses {
                    question_id
                    rating
                    question {
                        id
                        question
                    }
                }
            }
            paginatorInfo {
                total
                count
                perPage
                currentPage
                lastPage
                firstItem
                lastItem
                hasMorePages
            }
        }
    }
`;

export default {
    name: "smart-feedback-dashboard",
    components: {
        DatePicker,
        Pagination,
        IconEye,
    },
    data() {
        return {
            isLoading: false,
            dashboardData: {
                overall: {
                    total_sessions: 0,
                    total_quick_ratings: 0,
                    total_detailed_submissions: 0,
                    average_quick_rating: 0,
                    average_detailed_rating: 0,
                    response_rate: 0,
                    initiated_sessions: 0,
                    dismissed_sessions: 0,
                    // Optional: per-star quick rating counts from API (1–5)
                    rating_1_count: 0,
                    rating_2_count: 0,
                    rating_3_count: 0,
                    rating_4_count: 0,
                    rating_5_count: 0,
                },
                by_stakeholder: [],
                sentiment: {
                    positive: 0,
                    negative: 0,
                    neutral: 0,
                },
                trends: [],
                detailed_questions: [],
            },
            filters: {
                dateFrom: null,
                dateTo: null,
                stakeholderType: '',
                trendDays: 7,
            },
            roleOptions: [],
            submittersLoading: false,
            submitters: [],
            submitterPagination: {
                total: 0,
                count: 0,
                perPage: 10,
                currentPage: 1,
                lastPage: 1,
                firstItem: 0,
                lastItem: 0,
                hasMorePages: false,
            },
            showDetailModal: false,
            selectedSubmitter: null,
            detailLoading: false,
            detailSessions: [],
            detailPagination: {
                total: 0,
                count: 0,
                perPage: 10,
                currentPage: 1,
                lastPage: 1,
                firstItem: 0,
                lastItem: 0,
                hasMorePages: false,
            },
            // Accordion state for detailed question cards
            expandedDetailedQuestions: [],
        };
    },
    computed: {
        overallStats() {
            if (this.dashboardData && this.dashboardData.overall) {
                return this.dashboardData.overall;
            }
            return {
                total_sessions: 0,
                total_quick_ratings: 0,
                total_detailed_submissions: 0,
                average_quick_rating: 0,
                average_detailed_rating: 0,
                response_rate: 0,
                initiated_sessions: 0,
                dismissed_sessions: 0,
                rating_1_count: 0,
                rating_2_count: 0,
                rating_3_count: 0,
                rating_4_count: 0,
                rating_5_count: 0,
            };
        },
        detailedQuestionStats() {
            if (this.dashboardData && Array.isArray(this.dashboardData.detailed_questions)) {
                return this.dashboardData.detailed_questions;
            }
            return [];
        },
        overallStatCards() {
            return [
                {
                    key: 'total_sessions',
                    label: 'Total Sessions',
                    value: this.formatNumber(this.overallStats.total_sessions),
                    subtitle: 'All captured interactions',
                    icon: 'chart-line',
                    accent: 'from-blue-500 to-indigo-500',
                },
                {
                    key: 'total_quick_ratings',
                    label: 'Quick Ratings',
                    value: this.formatNumber(this.overallStats.total_quick_ratings),
                    subtitle: 'Instant sentiment inputs',
                    icon: 'chart-bar',
                    accent: 'from-emerald-500 to-teal-500',
                },
                {
                    key: 'total_detailed_submissions',
                    label: 'Detailed Submissions',
                    value: this.formatNumber(this.overallStats.total_detailed_submissions),
                    subtitle: 'In-depth qualitative feedback',
                    icon: 'comments',
                    accent: 'from-purple-500 to-pink-500',
                },
                {
                    key: 'average_quick_rating',
                    label: 'Avg Quick Rating',
                    value: this.formatRating(this.overallStats.average_quick_rating),
                    subtitle: '5-point scale',
                    icon: 'chart-line',
                    accent: 'from-indigo-500 to-sky-500',
                },
                {
                    key: 'average_detailed_rating',
                    label: 'Avg Detailed Rating',
                    value: this.formatRating(this.overallStats.average_detailed_rating),
                    subtitle: 'Comprehensive evaluations',
                    icon: 'chart-pie',
                    accent: 'from-amber-500 to-orange-500',
                },
                {
                    key: 'response_rate',
                    label: 'Response Rate',
                    value: `${this.formatPercentage(this.overallStats.response_rate)}%`,
                    subtitle: 'Engagement across sessions',
                    icon: 'chart-bar',
                    accent: 'from-fuchsia-500 to-rose-500',
                },
                {
                    key: 'initiated_sessions',
                    label: 'Initiated Sessions',
                    value: this.formatNumber(this.overallStats.initiated_sessions),
                    subtitle: 'Awaiting participant action',
                    icon: 'clipboard-list',
                    accent: 'from-slate-600 to-gray-800',
                },
                {
                    key: 'dismissed_sessions',
                    label: 'Dismissed Sessions',
                    value: this.formatNumber(this.overallStats.dismissed_sessions),
                    subtitle: 'Opted out interactions',
                    icon: 'inbox',
                    accent: 'from-red-500 to-red-600',
                },
            ];
        },
        trendSeries() {
            const trendData = this.dashboardData && this.dashboardData.trends ? this.dashboardData.trends : [];
            return [{
                name: 'Average Rating',
                data: trendData.map(t => t.average_rating),
            }];
        },
        trendChartOptions() {
            const trendCategories = this.dashboardData && this.dashboardData.trends ? this.dashboardData.trends : [];
            return {
                chart: {
                    type: 'line',
                    toolbar: { show: false },
                },
                xaxis: {
                    categories: trendCategories.map(t => t.date),
                },
                yaxis: {
                    min: 0,
                    max: 5,
                },
                stroke: {
                    curve: 'smooth',
                },
                colors: ['#3B82F6'],
            };
        },
        sentimentSeries() {
            const sentiment = this.dashboardData && this.dashboardData.sentiment ? this.dashboardData.sentiment : { positive: 0, negative: 0, neutral: 0 };
            return [
                sentiment.positive,
                sentiment.negative,
                sentiment.neutral,
            ];
        },
        sentimentChartOptions() {
            return {
                chart: {
                    type: 'pie',
                },
                labels: ['Positive', 'Negative', 'Neutral'],
                colors: ['#10B981', '#EF4444', '#6B7280'],
                legend: {
                    position: 'bottom',
                },
            };
        },
        stakeholderList() {
            if (this.dashboardData && Array.isArray(this.dashboardData.by_stakeholder)) {
                return this.dashboardData.by_stakeholder;
            }
            return [];
        },
        stakeholderCategories() {
            if (this.stakeholderList.length === 0) {
                return ['No Data'];
            }
            return this.stakeholderList.map(item => item.role_display_name || this.formatRoleLabel(item.role_name));
        },
        stakeholderSeries() {
            const stakeholders = this.stakeholderList;
            if (stakeholders.length === 0) {
                return [{
                    name: 'Average Rating',
                    data: [0],
                }];
            }
            return [{
                name: 'Average Rating',
                data: stakeholders.map(item => item && item.average_rating ? item.average_rating : 0),
            }];
        },
        stakeholderChartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                xaxis: {
                    categories: this.stakeholderCategories,
                },
                yaxis: {
                    min: 0,
                    max: 5,
                },
                colors: this.stakeholderBarColors,
                plotOptions: {
                    bar: {
                        borderRadius: 10,
                        borderRadiusApplication: 'end',
                        distributed: true,
                    },
                },
            };
        },
        stakeholderBarColors() {
            const palette = [
                '#3B82F6',
                '#10B981',
                '#F59E0B',
                '#EF4444',
                '#8B5CF6',
                '#EC4899',
                '#06B6D4',
                '#F97316',
                '#0EA5E9',
                '#84CC16',
            ];
            return this.stakeholderList.map((_, index) => palette[index % palette.length]);
        },
        stakeholderCards() {
            const accents = [
                { gradient: 'from-blue-500 to-indigo-500', icon: 'users' },
                { gradient: 'from-purple-500 to-pink-500', icon: 'comments' },
                { gradient: 'from-emerald-500 to-teal-500', icon: 'chart-line' },
                { gradient: 'from-amber-500 to-orange-500', icon: 'chart-pie' },
                { gradient: 'from-slate-600 to-gray-800', icon: 'clipboard-list' },
                { gradient: 'from-red-500 to-rose-500', icon: 'chart-bar' },
            ];
            return this.stakeholderList.map((item, index) => {
                const accent = accents[index % accents.length];
                return {
                    key: item.role_name || `stakeholder-${index}`,
                    title: item.role_display_name || this.formatRoleLabel(item.role_name),
                    totalSessions: this.formatNumber(item.total_sessions || 0),
                    totalRatings: this.formatNumber(item.total_ratings || 0),
                    averageRating: this.formatRating(item.average_rating),
                    responseRate: this.formatPercentage(item.response_rate),
                    responseRateWidth: this.computeProgressWidth(item.response_rate),
                    gradient: accent.gradient,
                    icon: accent.icon,
                };
            });
        },
        hasTrendData() {
            const trendData = this.dashboardData && this.dashboardData.trends ? this.dashboardData.trends : [];
            if (!trendData || trendData.length === 0) {
                return false;
            }
            return trendData.some(t => t.average_rating !== null && t.average_rating !== undefined && t.average_rating !== 0 && t.response_count > 0);
        },
        hasSentimentData() {
            const sentiment = this.dashboardData && this.dashboardData.sentiment ? this.dashboardData.sentiment : { positive: 0, negative: 0, neutral: 0 };
            const total = (sentiment.positive || 0) + (sentiment.negative || 0) + (sentiment.neutral || 0);
            return total > 0;
        },
        hasStakeholderChartData() {
            const stakeholders = this.stakeholderList;
            if (!stakeholders || stakeholders.length === 0) {
                return false;
            }
            return stakeholders.some(item => item && item.average_rating !== null && item.average_rating !== undefined && item.average_rating !== 0);
        },
        detailModalTitle() {
            if (this.selectedSubmitter && this.selectedSubmitter.user && this.selectedSubmitter.user.name) {
                return `Feedback Details - ${this.selectedSubmitter.user.name}`;
            }
            return 'Feedback Details';
        },
        submitterActiveCount() {
            return this.submitters.reduce((total, submitter) => total + (submitter.submitted_count || 0) + (submitter.detailed_submitted_count || 0), 0);
        },
        submitterDismissedCount() {
            return this.submitters.reduce((total, submitter) => total + (submitter.dismissed_count || 0), 0);
        },
        ratingBreakdown() {
            const total = Number(this.overallStats.total_quick_ratings) || 0;

            const counts = {
                5: Number(this.overallStats.rating_5_count) || 0,
                4: Number(this.overallStats.rating_4_count) || 0,
                3: Number(this.overallStats.rating_3_count) || 0,
                2: Number(this.overallStats.rating_2_count) || 0,
                1: Number(this.overallStats.rating_1_count) || 0,
            };

            const maxCount = Math.max(...Object.values(counts), 0) || 1;

            return [5, 4, 3, 2, 1].map(stars => {
                const count = counts[stars] || 0;
                const percentage = total > 0 ? (count / total) * 100 : 0;
                return {
                    stars,
                    count,
                    percentage: percentage.toFixed(1),
                    barWidth: this.computeRatingBarWidth(count, maxCount),
                };
            });
        },
        detailedQuestionBreakdown() {
            return this.detailedQuestionStats.map((q, index) => {
                const total = Number(q.total_ratings) || 0;
                const maxCount = Math.max(
                    Number(q.rating_1_count) || 0,
                    Number(q.rating_2_count) || 0,
                    Number(q.rating_3_count) || 0,
                    Number(q.rating_4_count) || 0,
                    Number(q.rating_5_count) || 0,
                    1
                );

                const rows = [5, 4, 3, 2, 1].map(stars => {
                    const key = `rating_${stars}_count`;
                    const count = Number(q[key]) || 0;
                    const percentage = total > 0 ? (count / total) * 100 : 0;
                    return {
                        stars,
                        count,
                        percentage: percentage.toFixed(1),
                        barWidth: this.computeRatingBarWidth(count, maxCount),
                    };
                });

                return {
                    id: q.question_id || index,
                    question: q.question || `Question ${index + 1}`,
                    average: this.formatRating(q.average_rating),
                    total: this.formatNumber(total),
                    rows,
                };
            });
        },
    },
    mounted() {
        this.initializeDashboard();
    },
    methods: {
        isDetailedQuestionExpanded(id) {
            return this.expandedDetailedQuestions.includes(id);
        },
        toggleDetailedQuestion(id) {
            if (this.isDetailedQuestionExpanded(id)) {
                this.expandedDetailedQuestions = this.expandedDetailedQuestions.filter(itemId => itemId !== id);
            } else {
                this.expandedDetailedQuestions = [...this.expandedDetailedQuestions, id];
            }
        },
        initializeDashboard() {
            this.loadDashboardData();
            this.loadSubmitters(true);
        },
        buildDateFilters() {
            return {
                date_from: this.filters.dateFrom ? `${this.filters.dateFrom} 00:00:00` : null,
                date_to: this.filters.dateTo ? `${this.filters.dateTo} 23:59:59` : null,
            };
        },
        handleFilterChange() {
            this.submitterPagination.currentPage = 1;
            this.detailPagination.currentPage = 1;
            this.loadDashboardData();
            this.loadSubmitters(true);
            if (this.showDetailModal) {
                this.loadDetailSessions(true);
            }
        },
        async loadDashboardData() {
            this.isLoading = true;
            const dateFilters = this.buildDateFilters();
            try {
                const result = await this.$apollo.query({
                    query: GET_DASHBOARD_STATS,
                    variables: {
                        date_from: dateFilters.date_from,
                        date_to: dateFilters.date_to,
                        stakeholder_type: this.filters.stakeholderType || null,
                        trend_days: this.filters.trendDays,
                    },
                    fetchPolicy: 'network-only',
                });

                if (result.data.getFeedbackDashboardStats.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: result.data.getFeedbackDashboardStats.message,
                    });
                    return;
                }

                this.dashboardData = result.data.getFeedbackDashboardStats.data;
                this.roleOptions = this.buildRoleOptions();
            } catch (error) {
                console.error('Error loading dashboard data:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load dashboard data',
                });
            } finally {
                this.isLoading = false;
            }
        },
        async loadSubmitters(resetPage = false) {
            if (resetPage) {
                this.submitterPagination.currentPage = 1;
            }
            this.submittersLoading = true;
            const dateFilters = this.buildDateFilters();
            try {
                const result = await this.$apollo.query({
                    query: GET_FEEDBACK_SUBMITTERS,
                    variables: {
                        date_from: dateFilters.date_from,
                        date_to: dateFilters.date_to,
                        stakeholder_type: this.filters.stakeholderType || null,
                        first: this.submitterPagination.perPage,
                        page: this.submitterPagination.currentPage,
                    },
                    fetchPolicy: 'network-only',
                });

                if (result.data.smartFeedbackSubmitters.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: result.data.smartFeedbackSubmitters.message,
                    });
                    return;
                }

                const payload = result.data.smartFeedbackSubmitters;
                this.submitters = payload.data || [];
                const info = payload.paginatorInfo || {};
                const submitterPerPage = info.perPage || this.submitterPagination.perPage || 0;
                const submitterCurrentPage = info.currentPage || 1;
                const submitterTotal = info.total || 0;
                const submitterCount = info.count || 0;

                this.submitterPagination = {
                    ...this.submitterPagination,
                    total: submitterTotal,
                    count: submitterCount,
                    perPage: submitterPerPage,
                    currentPage: submitterCurrentPage,
                    lastPage: info.lastPage || 1,
                    hasMorePages: info.hasMorePages || false,
                    firstItem: typeof info.firstItem === 'number'
                        ? info.firstItem
                        : (submitterCount > 0 && submitterPerPage > 0 ? ((submitterCurrentPage - 1) * submitterPerPage) + 1 : 0),
                    lastItem: typeof info.lastItem === 'number'
                        ? info.lastItem
                        : (submitterCount > 0 && submitterPerPage > 0 ? Math.min(submitterCurrentPage * submitterPerPage, submitterTotal) : 0),
                };
            } catch (error) {
                console.error('Error loading submitters:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load submitters',
                });
            } finally {
                this.submittersLoading = false;
            }
        },
        setSubmitterPage(page) {
            if (page < 1 || page === this.submitterPagination.currentPage || page > this.submitterPagination.lastPage) {
                return;
            }
            this.submitterPagination.currentPage = page;
            this.loadSubmitters();
        },
        openSubmitterDetails(submitter) {
            this.selectedSubmitter = submitter;
            this.detailPagination.currentPage = 1;
            this.showDetailModal = true;
            this.loadDetailSessions(true);
        },
        closeDetailModal() {
            this.showDetailModal = false;
            this.selectedSubmitter = null;
            this.detailSessions = [];
        },
        async loadDetailSessions(resetPage = false) {
            if (!this.selectedSubmitter || !this.selectedSubmitter.user) {
                return;
            }
            if (resetPage) {
                this.detailPagination.currentPage = 1;
            }
            this.detailLoading = true;
            const dateFilters = this.buildDateFilters();
            try {
                const result = await this.$apollo.query({
                    query: GET_FEEDBACK_USER_SESSIONS,
                    variables: {
                        user_id: this.selectedSubmitter.user.id,
                        date_from: dateFilters.date_from,
                        date_to: dateFilters.date_to,
                        stakeholder_type: this.filters.stakeholderType || null,
                        first: this.detailPagination.perPage,
                        page: this.detailPagination.currentPage,
                    },
                    fetchPolicy: 'network-only',
                });

                if (result.data.smartFeedbackUserSessions.error) {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: result.data.smartFeedbackUserSessions.message,
                    });
                    return;
                }

                const payload = result.data.smartFeedbackUserSessions;
                this.detailSessions = payload.data || [];
                const info = payload.paginatorInfo || {};
                const detailPerPage = info.perPage || this.detailPagination.perPage || 0;
                const detailCurrentPage = info.currentPage || 1;
                const detailTotal = info.total || 0;
                const detailCount = info.count || 0;

                this.detailPagination = {
                    ...this.detailPagination,
                    total: detailTotal,
                    count: detailCount,
                    perPage: detailPerPage,
                    currentPage: detailCurrentPage,
                    lastPage: info.lastPage || 1,
                    hasMorePages: info.hasMorePages || false,
                    firstItem: typeof info.firstItem === 'number'
                        ? info.firstItem
                        : (detailCount > 0 && detailPerPage > 0 ? ((detailCurrentPage - 1) * detailPerPage) + 1 : 0),
                    lastItem: typeof info.lastItem === 'number'
                        ? info.lastItem
                        : (detailCount > 0 && detailPerPage > 0 ? Math.min(detailCurrentPage * detailPerPage, detailTotal) : 0),
                };
            } catch (error) {
                console.error('Error loading user sessions:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load user feedback sessions',
                });
            } finally {
                this.detailLoading = false;
            }
        },
        setDetailPage(page) {
            if (page < 1 || page === this.detailPagination.currentPage || page > this.detailPagination.lastPage) {
                return;
            }
            this.detailPagination.currentPage = page;
            this.loadDetailSessions();
        },
        formatNumber(value) {
            if (value === null || value === undefined || value === '') {
                return '0';
            }
            const numeric = Number(value);
            if (Number.isNaN(numeric)) {
                return '0';
            }
            return numeric.toLocaleString();
        },
        formatRating(rating) {
            if (rating === null || rating === undefined || rating === '') {
                return '0.00';
            }
            const numeric = parseFloat(rating);
            if (Number.isNaN(numeric)) {
                return '0.00';
            }
            return numeric.toFixed(2);
        },
        formatPercentage(percentage) {
            if (percentage === null || percentage === undefined || percentage === '') {
                return '0.00';
            }
            const numeric = parseFloat(percentage);
            if (Number.isNaN(numeric)) {
                return '0.00';
            }
            const adjusted = numeric <= 1 ? numeric * 100 : numeric;
            return adjusted.toFixed(2);
        },
        computeProgressWidth(value) {
            if (value === null || value === undefined || value === '') {
                return '0%';
            }
            const numeric = parseFloat(value);
            if (Number.isNaN(numeric)) {
                return '0%';
            }
            const adjusted = numeric <= 1 ? numeric * 100 : numeric;
            const clamped = Math.max(0, Math.min(100, adjusted));
            return `${clamped}%`;
        },
        computeRatingBarWidth(count, maxCount) {
            if (!maxCount || maxCount <= 0) {
                return '0%';
            }
            const numeric = Number(count) || 0;
            const ratio = Math.max(0, Math.min(1, numeric / maxCount));
            return `${(ratio * 100).toFixed(1)}%`;
        },
        formatRoleLabel(roleName) {
            if (!roleName) {
                return 'Unknown';
            }
            return roleName
                .replace(/[_-]/g, ' ')
                .split(' ')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
        },
        buildRoleOptions() {
            const seen = new Set();
            return this.stakeholderList.reduce((acc, item) => {
                if (!item || !item.role_name || seen.has(item.role_name)) {
                    return acc;
                }
                seen.add(item.role_name);
                acc.push({
                    value: item.role_name,
                    label: item.role_display_name || this.formatRoleLabel(item.role_name),
                });
                return acc;
            }, []);
        },
        getSubmitterRoleLabel(submitter) {
            if (!submitter) {
                return '';
            }
            if (submitter.role_display_name) {
                return submitter.role_display_name;
            }
            if (submitter.role_name) {
                return this.formatRoleLabel(submitter.role_name);
            }
            return '';
        },
        formatDateTime(date) {
            if (!date) {
                return 'N/A';
            }
            const d = new Date(date);
            if (Number.isNaN(d.getTime())) {
                return date;
            }
            return d.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        formatStatus(status) {
            if (!status) {
                return 'Unknown';
            }
            return status
                .replace(/[_-]/g, ' ')
                .split(' ')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
        },
    },
};
</script>

<style scoped>
.ht-350 {
    height: 350px;
}

.accordion-enter-active,
.accordion-leave-active {
    transition: max-height 0.25s ease, opacity 0.25s ease;
    overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
    max-height: 0;
    opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
    max-height: 500px;
    opacity: 1;
}
</style>
